import { Footer } from './shared/Footer';
import { ExternalIcon } from '../utils/icons';
import { PageHead, PAGES } from '../utils/seo';

const About: React.FC = () => {
  return (
    <div className="h-[100svh] overflow-y-auto overflow-x-hidden relative text-left">
      <div className="lattice absolute inset-x-0 top-0 h-[30rem] pointer-events-none [mask-image:linear-gradient(to_bottom,black,transparent)]" />
      <PageHead page={PAGES.about} />
      <div className="relative mx-auto max-w-5xl px-6">
        <header className="flex items-center justify-between py-6">
          <a href="/" className="font-display text-wordmark">
            Marja<span className="text-peach">AI</span>
          </a>
          <a href="/ask" className="text-ui text-muted hover:text-peach">
            Ask a question
          </a>
        </header>
        <main>
          <article className="pt-16 md:pt-24 max-w-2xl rise">
            <h1 className="balance font-display text-display-lg-sm md:text-display-lg">
              Made by Shias, for Shias.
            </h1>
            <div className="mt-10 space-y-6 text-body-lg text-muted">
              <p>
                For years, believers have struggled to get the right answers to
                their questions. Most of us never do, unless we know a scholar
                personally, have the patience to wait for a manual reply from
                His Eminence’s office, or can navigate the websites and books.
              </p>
              <p>
                And that's without the awkward questions people prefer not to
                ask. That's why we built Marja AI: a group of believers who
                wanted to take the problem on head first.
              </p>
              <p className="text-ink">
                Our goal is to make Islamic rulings easier to reach.
              </p>
            </div>
          </article>

          <section className="mt-20 max-w-2xl">
            <h2 className="balance font-display text-heading">
              Why asking matters
            </h2>
            <div className="mt-6 space-y-6 text-body-lg text-muted">
              <p>
                Asking is not a sign of weak faith. It is how knowledge has
                always been passed on.
              </p>
              <p>
                Most of us follow a Marja' because we cannot derive the rulings
                ourselves. But following only works if we know what the ruling
                actually is. A small detail, like whether you are travelling or
                settled, or whether a doubt came before or after an act, can
                change the answer completely.
              </p>
              <p>
                Guessing, or relying on what a friend heard once, is how small
                mistakes in prayer, fasting and money matters build up over
                years. A two-minute question now is cheaper than a long make-up
                later.
              </p>
              <p>
                And no question is too small or too awkward. If you wondered
                about it, someone else did too.
              </p>
            </div>
          </section>

          <section className="mt-20 max-w-2xl">
            <h2 className="balance font-display text-heading">
              Asking a good question
            </h2>
            <ul className="mt-6 divide-y divide-line border-y border-line !list-none !pl-0 text-body-lg">
              {[
                [
                  'Give the situation',
                  'Mention whether you are travelling, a student, married, working. The ruling often depends on it.',
                ],
                [
                  'One question at a time',
                  'Short, focused questions get clearer answers than a long story with five problems inside it.',
                ],
                [
                  'Say what already happened',
                  'Ruling for the future and ruling for something already done are often different.',
                ],
                [
                  'Open the sources',
                  'Every answer links to the original text. Read it, especially before acting on anything important.',
                ],
              ].map(([title, body]) => (
                <li key={title} className="py-5">
                  <div className="text-ink">{title}</div>
                  <div className="mt-1 text-muted">{body}</div>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-20 max-w-2xl">
            <h2 className="balance font-display text-heading">
              Official sources
            </h2>
            <div className="mt-6 space-y-6 text-body-lg text-muted">
              <p>
                Marja AI is a way to find and read the rulings, not a
                replacement for your Marja' or His Eminence’s office. Right now
                we answer from the published rulings of Ayatollah Sistani. For
                anything that matters, or anything we cannot answer, go to the
                official sources:
              </p>
              <ul className="divide-y divide-line border-y border-line !list-none !pl-0">
                {[
                  [
                    'sistani.org',
                    'https://www.sistani.org',
                    'The official website of the office of Grand Ayatollah Sistani.',
                  ],
                  [
                    'najaf.org',
                    'https://www.najaf.org',
                    'His Eminence’s office in Najaf, with the rulings and a way to submit questions.',
                  ],
                ].map(([name, href, desc]) => (
                  <li key={href} className="py-5">
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-peach underline underline-offset-4 hover:text-peach-soft"
                    >
                      {name}
                      <ExternalIcon />
                    </a>
                    <div className="mt-1 text-muted">{desc}</div>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mt-20 max-w-2xl">
            <h2 className="balance font-display text-heading">
              What Marja AI is, and isn't
            </h2>
            <div className="mt-6 space-y-6 text-body-lg text-muted">
              <p>
                It searches the published books and Q&amp;As, quotes the
                relevant passages, and explains them in plain language. If the
                answer isn't in those texts, it should say so rather than guess.
              </p>
              <p>
                It is an AI, so it can still make mistakes. It does not issue
                rulings and it cannot see your full situation. Treat it as a
                well-read friend who points you to the right page: a starting
                point, not a ruling. Always confirm with the sources or a
                scholar yourself.
              </p>
              <p>
                Want to help, or spotted a mistake? Write to us at{' '}
                <a
                  href="mailto:salam@marja.ai"
                  className="text-peach underline underline-offset-4 hover:text-peach-soft"
                >
                  salam@marja.ai
                </a>
                .
              </p>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default About;
